/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { t } from 'i18next';

// Component imports
import { Button, Col, Grid, Menu, Row, Input, Skeleton, Space, Spin, Typography, Collapse, Select, Empty, Popconfirm, Pagination, Tooltip } from 'antd';
import { AlertOutlined, FieldNumberOutlined, SearchOutlined, FrownOutlined, HeartOutlined, HomeOutlined, StopOutlined, TrophyOutlined, WalletOutlined, LaptopOutlined, SecurityScanOutlined, SkinOutlined, CalendarOutlined, IdcardOutlined, CarryOutOutlined, BookOutlined, DollarOutlined, ThunderboltOutlined, WarningOutlined, DislikeOutlined, ToolOutlined, TeamOutlined, ExceptionOutlined, ClockCircleOutlined, CalculatorOutlined, DeploymentUnitOutlined, GlobalOutlined, EyeOutlined, ScheduleOutlined, StarOutlined } from '@ant-design/icons';
import RichTextEditor from '../Forms/Fields/RichTextEditor/RichTextEditor';
import CreateHRWizKnowledgeBaseArticleModal from '../Modals/CreateHRWizKnowledgeBaseArticleModal';
import { PlusIcon } from '../Icons/PlusIcon';
import { ArrowLeftIcon } from '../Icons/ArrowLeftIcon';

// Misc imports
import { useFetchBotQuery, useFetchBotsQuery } from '../../store/bots/botsApi';
import { useFetchKnowledgeBasesArticlesQuery, useFetchKnowledgeBaseArticlesTagsQuery, useDeleteKnowledgeBaseArticleMutation, useFetchKnowledgeBasesQuery, useUpdateKnowledgeBaseArticleMutation } from '../../store/knowledgeBases/knowledgeBasesApi';
import { HRWIZ_TABS } from '../../screens/HRWizConsoleScreen';
import { KNOWLEDGE_BASE_TYPES, KNOWLEDGE_BASE_SPACES } from '../../constants/main';
import { useFetchBotDeploymentPreviewQuery } from '../../store/botDeployments/botDeploymentsApi';
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import useTheme from '../../utils/hooks/useTheme';
import { ARTICLE_TITLE_MAX_LENGTH } from '../../constants/knowledgeBases';

const EMPTY_PAGINATED_RESPONSE = {
  items: [],
  page: 0,
  pageSize: 20,
  total: 0
};

// Constant declarations
const TOPICS_ICONS = {
  'IT & Security': <SecurityScanOutlined />,
  'Benefits': <TrophyOutlined />,
  'Etiquette & Dress code': <SkinOutlined />,
  'Emergency': <AlertOutlined />,
  'Harassment': <StopOutlined />,
  'Holidays': <HomeOutlined />,
  'Medical Health': <HeartOutlined />,
  'PTO & Vacation': <CalendarOutlined />,
  'Addresses & Phone numbers': <FieldNumberOutlined />,
  'Payroll': <WalletOutlined />,
  'Privacy & Personnel Records': <IdcardOutlined />,
  'Sick Leave': <FrownOutlined />,
  'Attendance & Absence': <CarryOutOutlined />,
  'Code of Conduct': <BookOutlined />,
  'Company Equipment': <LaptopOutlined />,
  'Compensation': <DollarOutlined />,
  'Conflict at Work': <ThunderboltOutlined />,
  'Conflicts of Interest': <WarningOutlined />,
  'Discrimination': <DislikeOutlined />,
  'Employment': <ToolOutlined />,
  'Management': <TeamOutlined />,
  'NDA': <ExceptionOutlined />,
  'Other Leave': <LaptopOutlined />,
  'Overtime': <ClockCircleOutlined />,
  'Reimbursement': <CalculatorOutlined />,
  'Remote Work': <DeploymentUnitOutlined />,
  'Travel': <GlobalOutlined />,
  'Vision Mission': <EyeOutlined />,
  'Work Schedule & Breaks': <ScheduleOutlined />,
  'Other': <StarOutlined />
};

export const KNOWN_TOPICS_KEYS = {
  'it__security': 'IT & Security',
  'benefits': 'Benefits',
  'etiquette__dress_code': 'Etiquette & Dress code',
  'emergency': 'Emergency',
  'harassment': 'Harassment',
  'holidays': 'Holidays',
  'medical_health': 'Medical Health',
  'pto__vacation': 'PTO & Vacation',
  'addresses__phone_numbers': 'Addresses & Phone numbers',
  'payroll': 'Payroll',
  'privacy__personnel_records': 'Privacy & Personnel Records',
  'sick_leave': 'Sick Leave',
  'attendance__absence': 'Attendance & Absence',
  'code_of_conduct': 'Code of Conduct',
  'company_equipment': 'Company Equipment',
  'compensation': 'Compensation',
  'conflict_at_work': 'Conflict at Work',
  'conflicts_of_interest': 'Conflicts of Interest',
  'discrimination': 'Discrimination',
  'employment': 'Employment',
  'management': 'Management',
  'nDA': 'NDA',
  'other_leave': 'Other Leave',
  'overtime': 'Overtime',
  'reimbursement': 'Reimbursement',
  'remote_work': 'Remote Work',
  'travel': 'Travel',
  'vision_mission': 'Vision Mission',
  'work_schedule__breaks': 'Work Schedule & Breaks',
  'other': 'Other'
};

const findKnownTopicKey = (value) => {
  return Object.keys(KNOWN_TOPICS_KEYS).find(key => KNOWN_TOPICS_KEYS[key] === value);
};

const HRWizArticles = () => {
  const { orgId, tenantId } = useParams();
  const { hrwizPanelHeight } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasExceededTitleCharacterLength, setHasExceededTitleCharacterLength] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobileScreen = screens.xs && !screens.sm;
  const searchInputRef = useRef(null);

  const [hasLoadedTagsOnce, setHasLoadedTagsOnce] = useState(false);
  const [activeKnowledgeBases, setActiveKnowledgeBases] = useState([]);

  // Topics that are displayed in the sidebar
  const [displayTopics, setDisplayTopics] = useState([]);
  // Topics that are not recognized by the system but are used in the articles
  const [unrecognizedTopics, setUnrecognizedTopics] = useState({});

  // HRWiz Tenant API data
  const { data: bots, isLoading: isLoadingBots } = useFetchBotsQuery({ orgId });
  const { refetch: refetchBot } = useFetchBotQuery({ orgId, tenantId }, { skip: !tenantId });
  useFetchBotDeploymentPreviewQuery({ orgId, tenantId }, { skip: !tenantId });
  const { data: _knowledgeBases, isLoading: isLoadingKnowledgeBases } = useFetchKnowledgeBasesQuery(
    {
      orgId,
      urlParams: {
        space: KNOWLEDGE_BASE_SPACES.EX,
        type: [
          KNOWLEDGE_BASE_TYPES.INTEGRATION,
          KNOWLEDGE_BASE_TYPES.INTERNAL
        ],
        pageSize: 1000,
        includeTenants: true,
        order: 'DESC',
        sortBy: 'createdAt'
      }
    }
  );
  const primaryBotLanguage = bots?.items.find(bot => bot.id === tenantId)?.settings?.languages?.primary || 'en';

  const { data: _articlesTags, isLoading: isLoadingKbArticlesTags, isFetching: isFetchingKbArticlesTags, isSuccess: isSuccessKbArticlesTags, isError: isErrorKbArticlesTags, refetch: refetchTags } = useFetchKnowledgeBaseArticlesTagsQuery(
    {
      orgId,
      urlParams: {
        knowledgeBaseIds: activeKnowledgeBases.join(','),
        languageCode: primaryBotLanguage,
        ...(searchParams.get('searchTerm') && { searchTerm: searchParams.get('searchTerm') })
      }
    },
    { skip: !activeKnowledgeBases.length }
  );

  const articlesTags = useMemo(() => {
    return activeKnowledgeBases.length ? _articlesTags : [];
  }, [_articlesTags, activeKnowledgeBases]);

  const tagsFilter = displayTopics.find(t => t.key === searchParams.get('q'))?.displayName || (searchParams.get('q') ? KNOWN_TOPICS_KEYS[searchParams.get('q')] || searchParams.get('q') : '');
  const { data: _kbArticles, isFetching: isLoadingKbArticles, refetch: refetchArticles } = useFetchKnowledgeBasesArticlesQuery(
    {
      orgId,
      urlParams: {
        ...(searchParams.get('searchTerm') && { searchTerm: searchParams.get('searchTerm') }),
        knowledgeBaseIds: activeKnowledgeBases.join(','),
        languageCode: primaryBotLanguage,
        includeKnowledgeBase: true,
        page: searchParams.get('page') || 0,
        pageSize: searchParams.get('pageSize') || 20,
        tags: encodeURIComponent(tagsFilter),
        noTags: tagsFilter?.toLowerCase() === 'other' ? 'true' : 'false',
        sortBy: 'ordinal',
        order: 'asc'
      }
    },
    { skip: !activeKnowledgeBases.length || (!articlesTags?.length && !searchParams.get('searchTerm')) || !searchParams.get('q') }
  );

  const activeKnowledgeBasesList = useMemo(() => {
    const arrayOfActiveKBs = [];
    _knowledgeBases?.items?.forEach((kb) => {
      if(activeKnowledgeBases?.includes(kb?.id)) {
        arrayOfActiveKBs.push({
          id: kb?.id,
          label: kb?.name
        });
      }
    });
    return arrayOfActiveKBs;
  }, [_knowledgeBases, activeKnowledgeBases]);

  const kbArticles = useMemo(() => {
    return activeKnowledgeBases.length ? _kbArticles : EMPTY_PAGINATED_RESPONSE;
  }, [_kbArticles, activeKnowledgeBases]);

  const [updateArticle, { isLoading: isUpdatingArticle }] = useUpdateKnowledgeBaseArticleMutation();
  const [deleteArticle, { isLoading: isDeletingArticle }] = useDeleteKnowledgeBaseArticleMutation();

  // UI state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCreateKBArticleModalOpen, setIsCreateKBArticleModalOpen] = useState(false);
  /*
   * {
   *    [articleId]: [article]
   * }
   */
  const [editedArticles, setEditedArticles] = useState({});
  const [activeKeys, setActiveKeys] = useState([]);

  const updateUrlSearchParams = (searchTerm) => {
    const urlSearchParams = new URLSearchParams(location.search);

    if (searchTerm) {
      urlSearchParams.set('searchTerm', encodeURIComponent(searchTerm));
    } else {
      urlSearchParams.set('searchTerm', encodeURIComponent(''));
    }

    setActiveKeys([]);

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const onArticleCreated = async () => {
    await refetchArticles();
    await refetchTags();
    await refetchBot();
  };
  const onTabChange = (activeKey) => {
    navigate({
      pathname: location.pathname,
      search: `?tab=${activeKey}`
    }, { replace: true });
  };

  const focusSearchInput = () => {
    if(searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const debouncedOnChange = useDebouncedCallback(updateUrlSearchParams);
  const debouncedOnSearchFocus = useDebouncedCallback(focusSearchInput, 200);

  const TOPICS_KEYS = useMemo(() => {
    return {
      ...KNOWN_TOPICS_KEYS,
      ...unrecognizedTopics
    };
  }, [unrecognizedTopics]);

  const handlePanelChange = (key) => {
    setActiveKeys(key);
  };

  useEffect(() => {
    if (isSuccessKbArticlesTags || isErrorKbArticlesTags) {
      setHasLoadedTagsOnce(true);
    }
  }, [isSuccessKbArticlesTags, isErrorKbArticlesTags]);

  useEffect(() => {
    const activeKBs = _knowledgeBases?.items?.filter(tmp => {
      return tmp.tenantAssociations?.find((ta) => ta.tenantId === tenantId) != null && tmp.type !== KNOWLEDGE_BASE_TYPES.SHARED;
    }) ?? [];

    const activeKBsIds = activeKBs.map(tmp => tmp.id);
    setActiveKnowledgeBases(activeKBsIds);
  }, [_knowledgeBases, orgId, tenantId]);

  useEffect(() => {
    if (isLoadingKbArticlesTags || !hasLoadedTagsOnce) {
      return;
    }

    const topics = new Set();
    const _unrecognizedTopics = {};
    const shouldHaveOther = activeKnowledgeBases.length > 0 && articlesTags.includes('Other');

    articlesTags?.forEach((tag) => {
      const key = findKnownTopicKey(tag);
      if (key) {
        topics.add(key);
      } else {
        topics.add(tag);
        _unrecognizedTopics[tag] = tag;
      }
    });
    setUnrecognizedTopics(_unrecognizedTopics);

    if (topics.has('other')) {
      // we want "other" to be always last
      topics.delete('other');
    }

    const filteredTopics = [...topics]
      .map(key => ({ displayName: KNOWN_TOPICS_KEYS[key] || key, key }))
      .sort((a, b) => a.displayName === 'other' ? 1 : b.displayName === 'other' ? -1 : a.displayName >= b.displayName ? 1 : -1);
    if (shouldHaveOther) {
      filteredTopics.push({ displayName: 'Other', key: 'other' });
    }
    setDisplayTopics(filteredTopics);

    const q = searchParams.get('q');
    let urlParamExists = q && filteredTopics.find(tmp => tmp.key === q) != null;
    if (!urlParamExists && filteredTopics.length > 0) {
      setSearchParams({ ...Object.fromEntries(searchParams.entries()), q: filteredTopics[0].key });
    }
  }, [tenantId, articlesTags, isLoadingKbArticlesTags, hasLoadedTagsOnce, activeKnowledgeBases]);

  useEffect(() => {
    if (kbArticles?.items && kbArticles.items.length === 1 && !searchParams.get('searchTerm')) {
      setActiveKeys([kbArticles.items[0].id]);
    } else {
      setActiveKeys([]);
    }
  }, [kbArticles]);

  useEffect(() => {
    if (isMobileScreen) {
      setIsSidebarOpen(false);
    }
  }, [isMobileScreen]);

  const onPageChange = (page, pageSize) => {
    const newPage = parseInt(page) - 1;
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: newPage
    });
  };

  return (
    <div
      style={{ height: hrwizPanelHeight }}
      className="overflow-hidden p-0 d-flex flex-column"
      bodyStyle={{ height: '100%', overflow: 'hidden' }}
    >
      <Row gutter={[16, 16]} className="border-bottom py-4 flex-shrink-0">
        <Col flex="auto">
          <Input
            size="large"
            ref={searchInputRef}
            placeholder={t('Search articles')}
            allowClear
            defaultValue={searchParams.get('searchTerm') ? decodeURIComponent(searchParams.get('searchTerm')) : ''}
            prefix={<SearchOutlined className="text-muted" />}
            style={{ maxWidth: 280 }}
            onChange={(e) => {
              debouncedOnChange(e.target.value);
              debouncedOnSearchFocus();
            }}
          />
        </Col>

        <Col flex="none">
          <Space>
            <Tooltip title={!activeKnowledgeBases?.length > 0 ? 'Add or connect a Source first' : ''} >
              <Button
                icon={<PlusIcon />}
                type="primary"
                className="border-0"
                size="large"
                disabled={!activeKnowledgeBases?.length > 0}
                onClick={() => setIsCreateKBArticleModalOpen(true)}
              >
                {t('Add Article')}
              </Button>
            </Tooltip>

            <Button
              size="large"
              className="text-left"
              onClick={() => onTabChange(HRWIZ_TABS.SOURCES)}
            >
              {t('Sources')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Row
        className="flex-auto"
        align="middle"
        wrap={false}
        style={{ minHeight: 0 }}
      >
        <Col
          className={classNames('h-100 pr-0 overflow-x-hidden position-relative', {
            'd-flex flex-column': isSidebarOpen || !isMobileScreen,
            'd-none': !isSidebarOpen && isMobileScreen
          })}
          flex={!isMobileScreen ? '0 1 440px' : '1 1 100%'}
        >
          <Typography.Title level={5} className="px-4 pt-5 pb-2">{t('Topic')}</Typography.Title>
          <div className="flex-grow-1 overflow-x-hidden">
            {
              isLoadingBots ? (
                <Skeleton active title={false} className="hrwiz-console-skeleton" paragraph={{ rows: 12, width: '100%' }}/>
              ) : (
                <Fragment>
                  {
                    !articlesTags?.length && !isFetchingKbArticlesTags ? (
                      <Row className="h-100" align="middle" justify="center">
                        <Empty className="text-muted" description={t('No data')} />
                      </Row>
                    ) : (
                      <Menu
                        selectedKeys={searchParams.get('q')}
                        activeKey={searchParams.get('q')}
                        items={displayTopics.map(aa => (
                          {
                            key: aa.key,
                            label: aa.displayName.replaceAll('_', ' '),
                            icon: TOPICS_ICONS[aa.displayName] || <LaptopOutlined />
                          }
                        ))}
                        onClick={({ key }) => {
                          const entries = { ...Object.fromEntries(searchParams.entries()) };
                          if (entries.page) {
                            delete entries.page;
                          }
                          setSearchParams({ ...entries, q: key });
                          setTimeout(() => {
                            setIsSidebarOpen(false);
                          });
                        }}
                        className="hrwiz-console-menu scrollbar-custom flex-auto"
                        mode="inline"
                      />
                    )
                  }
                </Fragment>
              )
            }
          </div>
        </Col>

        <Col
          className={
            isSidebarOpen && isMobileScreen
              ? 'd-none' : `h-100 ${isMobileScreen ? '' : 'border-left'} pl-0 w-100`
          }
          flex="auto"
        >
          {
            isLoadingBots || isLoadingKnowledgeBases || isLoadingKbArticles || isLoadingKbArticlesTags ? (
              <Row
                className="h-100"
                justify="center"
                align="middle"
              >
                <Spin size="large" />
              </Row>
            ) : (
              <Row className="flex-column h-100" wrap={false}>
                <div className="flex-auto overflow-y-auto w-100 px-6 py-5">
                  <Space align="center" className="mb-3" size={12}>
                    {
                      isMobileScreen ? (
                        <Button
                          size="small"
                          type="primary"
                          icon={<ArrowLeftIcon />}
                          onClick={() => setIsSidebarOpen(true)}
                        />
                      ) : null
                    }

                    <Typography.Title level={5} className="mb-0 fw-bold">
                      {t('Articles')}
                    </Typography.Title>
                  </Space>

                  {
                    !kbArticles?.items?.length && (
                      <Row className="h-100" align="middle" justify="center">
                        <Empty className="text-muted" description={t('No data')} />
                      </Row>
                    )
                  }

                  <div className="flex-auto overflow-y-auto w-100 pt-5 pb-0">
                    <Collapse className="hrwiz-articles-collapse scrollbar-custom" destroyInactivePanel={true} activeKey={activeKeys} accordion={true} expandIconPosition={'start'} onChange={handlePanelChange}>
                      {
                        kbArticles?.items?.map(article => {
                          const editedArticle = editedArticles[article.id] || {};
                          const combinedArticle = {
                            ...article,
                            ...editedArticle
                          };
                          const articleHasChanged = editedArticles[combinedArticle.id] != null;

                          return (
                            <Collapse.Panel header={combinedArticle.content?.find(a => a.languageCode === primaryBotLanguage)?.title} key={combinedArticle.id}>
                              <Input
                                status={combinedArticle.content?.find(a => a.languageCode === primaryBotLanguage)?.title.length > ARTICLE_TITLE_MAX_LENGTH ? 'error' : null}
                                defaultValue={combinedArticle.content?.find(a => a.languageCode === primaryBotLanguage)?.title}
                                onChange={(e) => {
                                  if (e.target.value.length > ARTICLE_TITLE_MAX_LENGTH) {
                                    setHasExceededTitleCharacterLength(true);
                                  }

                                  if (hasExceededTitleCharacterLength && e.target.value.length <= ARTICLE_TITLE_MAX_LENGTH) {
                                    setHasExceededTitleCharacterLength(false);
                                  }

                                  const newContent = [...(combinedArticle.content || [])];
                                  const contentIndex = newContent.findIndex(a => a.languageCode === primaryBotLanguage);
                                  if (contentIndex > -1) {
                                    newContent[contentIndex] = {
                                      ...newContent[contentIndex],
                                      title: e.target.value
                                    };
                                  } else {
                                    newContent.push({
                                      languageCode: primaryBotLanguage,
                                      title: e.target.value,
                                      body: ''
                                    });
                                  }
                                  setEditedArticles({
                                    ...editedArticles,
                                    [combinedArticle.id]: {
                                      ...editedArticle,
                                      content: newContent
                                    }
                                  });
                                }}
                                className="mb-5"
                              />

                              <RichTextEditor
                                placeholder="Enter a response"
                                style={{ maxHeight: 600 }}
                                value={combinedArticle.content?.find(tmp => tmp.languageCode === primaryBotLanguage)?.body}
                                shouldAutoFocus={false}
                                onChange={(convertedValue, delta, source, editor) => {
                                  const newContent = [...(combinedArticle.content || [])];
                                  const contentIndex = newContent.findIndex(tmp => tmp.languageCode === primaryBotLanguage);
                                  if (contentIndex > -1) {
                                    newContent[contentIndex] = {
                                      ...newContent[contentIndex],
                                      body: convertedValue
                                    };
                                  } else {
                                    newContent.push({
                                      languageCode: primaryBotLanguage,
                                      body: convertedValue
                                    });
                                  }
                                  setEditedArticles({
                                    ...editedArticles,
                                    [combinedArticle.id]: {
                                      ...editedArticle,
                                      content: newContent
                                    }
                                  });
                                }}
                              />

                              <Typography.Text level={5} className="text-muted mt-1 float-right">
                                {t('Source:')} {combinedArticle.knowledgeBase?.name}
                              </Typography.Text>

                              <Row className="w-100 pt-5">
                                <Col span={12}>
                                  <Select
                                    defaultValue={searchParams.get('q') ?? TOPICS_KEYS[combinedArticle.content?.find(tmp => tmp.languageCode === primaryBotLanguage)?.tags?.[0] ?? 'other']}
                                    className="w-100"
                                    onChange={(value) => {
                                      const newContent = [...(combinedArticle.content || [])];
                                      const contentIndex = newContent.findIndex(tmp => tmp.languageCode === primaryBotLanguage);
                                      if (contentIndex > -1) {
                                        newContent[contentIndex] = {
                                          ...newContent[contentIndex],
                                          tags: [TOPICS_KEYS[value]]
                                        };
                                      }
                                      setEditedArticles({
                                        ...editedArticles,
                                        [combinedArticle.id]: {
                                          ...editedArticle,
                                          content: newContent
                                        }
                                      });
                                    }}
                                    options={
                                      Object.keys(TOPICS_KEYS)
                                        .map(tmp => ({ label: TOPICS_KEYS[tmp], value: tmp }))
                                        .sort((a, b) => a.value === 'other' ? 1 : b.value === 'other' ? -1 : a.label >= b.label ? 1 : -1)
                                    }
                                  />
                                </Col>

                                <Col span={12}>
                                  <Button
                                    size="large"
                                    className="d-block float-right"
                                    type="primary"
                                    disabled={isUpdatingArticle || !articleHasChanged || hasExceededTitleCharacterLength}
                                    loading={isUpdatingArticle}
                                    onClick={async () => {
                                      await updateArticle({
                                        orgId: orgId,
                                        knowledgeBaseId: combinedArticle?.knowledgeBase?.id,
                                        id: combinedArticle.id,
                                        data: { content: combinedArticle.content }
                                      }).unwrap();
                                      await refetchArticles();
                                      await refetchBot();
                                    }}
                                  >
                                    {t('Save Changes')}
                                  </Button>

                                  <Popconfirm
                                    title={t('Are you sure you want to delete this article?')}
                                    okText={t('Yes')}
                                    cancelText={t('No')}
                                    placement="topRight"
                                    onConfirm={async () => {
                                      await deleteArticle({
                                        orgId,
                                        knowledgeBaseId: combinedArticle?.knowledgeBase?.id,
                                        id: combinedArticle.id
                                      }).unwrap();
                                      await refetchArticles();
                                      await refetchBot();
                                    }}
                                  >
                                    <Button
                                      size="large"
                                      className="d-block float-right mr-3"
                                      // type="danger"
                                      loading={isDeletingArticle}
                                    >
                                      {t('Delete article')}
                                    </Button>
                                  </Popconfirm>
                                </Col>
                              </Row>
                            </Collapse.Panel>
                          );
                        })
                      }
                    </Collapse>
                  </div>
                </div>

                <div className={'px-6 pt-6 pb-2 w-100 float-right flex-shrink-0'}>
                  <Pagination
                    className={'float-right'}
                    onChange={onPageChange}
                    total={kbArticles?.total || 0}
                    defaultCurrent={1}
                    current={(+searchParams.get('page') ?? 1) + 1}
                    pageSize={kbArticles?.pageSize || 0}
                    showSizeChanger={false}
                    size={'default'}
                  />
                </div>
              </Row>
            )
          }
        </Col>
      </Row>

      <CreateHRWizKnowledgeBaseArticleModal
        activeKBs={activeKnowledgeBasesList}
        isModalOpen={isCreateKBArticleModalOpen}
        topics={TOPICS_KEYS}
        closeModal={() => setIsCreateKBArticleModalOpen(false)}
        onCreated={async () => await onArticleCreated()}
      />
    </div>
  );
};

export default HRWizArticles;
